<template>
    <div class="trip-pattern-summary text-grey-darken-5">
        <div class="my-2">
            <div v-if="topLabel" class="text-h6 text-grey-darken-2" v-text="topLabel" />
            <v-row dense align="center" class="flex-nowrap" :style="{ marginTop: !isNonTransit ? -10 : undefined }">
                <v-col v-if="!isNonTransit" cols="auto" class="flex-shrink-1 d-flex align-center text-truncate">
                    <quay-signage
                        :icon="getTripModeIcon(firstTransitLeg?.tripMode, firstTransitLeg?.transitInfo)"
                        :color="getTripModeIconColor(firstTransitLeg?.tripMode)"
                        icon-size="22"
                        class="mr-2 flex-shrink-0"
                        style="margin-top: 6px"
                        size="30"
                    />
                    <div class="text-h5 font-weight-bold text-truncate" v-text="firstTransitLeg?.from.name" />
                    <platform-label :title="firstTransitLeg?.from.quay?.publicCode" size="24" class="ml-1 flex-shrink-0" />
                </v-col>
                <v-spacer v-if="!isNonTransit" />
                <v-col v-if="firstLegDuration" cols="auto" class="d-flex text-no-wrap">
                    <v-icon v-if="firstWalkingLeg" icon="kdi-foot" size="21" />
                    <div class="text-h5 font-weight-regular" v-text="firstLegDuration" />
                </v-col>
            </v-row>
        </div>
        <div v-if="!isNonTransit && firstTransitLeg" class="my-2 bottom-div">
            <div class="text-h6 text-grey-darken-2" v-text="getTripStartText(firstTransitLeg.tripMode, firstTransitLeg.transitInfo)" />
            <div class="d-flex">
                <route-number :title="firstTransitLeg.lineName" fixed-size class="mr-2" />
                <trip-headsign :title="titleAndSubtitle.title" :subtitle="titleAndSubtitle.subtitle" />
                <v-spacer />
                <div class="d-flex align-center">
                    <realtime-indicator v-if="firstTransitLeg.realtime && firstTransitLeg.isStartingSoon" class="mr-1" />
                    <div
                        class="text-h5 text-no-wrap"
                        :class="firstTransitLeg.realtime && firstTransitLeg.isStartingSoon ? 'font-weight-bold' : 'font-weight-regular'"
                        v-text="firstTransitLegTime"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { TripMode, type TripPatternDto } from '@/types/webapi';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getTripModeIcon, getTripModeIconColor, splitHeadsign, useDurationHelpers } from '@/features/common/composables';
import { TripHeadsign, RealtimeIndicator } from '@/features/common/components';
import { QuaySignage, PlatformLabel, RouteNumber } from '@geta/kolumbus-frontend/components';
import { toLocaleTimeString } from '@geta/kolumbus-frontend/composables';
import { differenceInSeconds, isDate } from 'date-fns';
import { useTripModeHelper } from '@/features/trips/composables';
import { isTransitMode } from '@/features/trips/helpers';
import { useDisplay } from 'vuetify';

// min threshold before switching to relative time i.e. 15 mins
const relativeTimeThreshold = 900;

interface Props {
    modelValue: TripPatternDto;
    now?: Date;
    loading?: boolean | string;
}

const props = defineProps<Props>();
const { t } = useI18n();
const { humanizeDuration } = useDurationHelpers();
const { mobile: isMobile } = useDisplay();
const { getTripStartText } = useTripModeHelper();
const isNonTransit = computed(() => props.modelValue.legs?.length === 1 && !isTransitMode(props.modelValue.legs[0].tripMode));

const firstTransitLeg = computed(() => props.modelValue.legs?.find(x => x.tripMode !== TripMode.Foot));
const firstTransitLegTime = computed(() => {
    if (!firstTransitLeg.value) return undefined;

    const { aimedStartTime, expectedStartTime, isStartingSoon, realtime } = firstTransitLeg.value;
    const startTime = isStartingSoon || realtime ? expectedStartTime : aimedStartTime;
    if (!isDate(startTime)) return undefined;

    if (isDate(props.now)) {
        // if difference with startTime is < 15 minutes, display as relative time
        const diffInSeconds = differenceInSeconds(startTime, props.now!);
        if (diffInSeconds < relativeTimeThreshold) {
            return humanizeDuration(diffInSeconds);
        }
    }

    return toLocaleTimeString(startTime);
});
const titleAndSubtitle = computed(() => splitHeadsign(firstTransitLeg.value?.tripHeadsign));

const firstWalkingLeg = computed(() => (props.modelValue.legs?.[0].tripMode === TripMode.Foot ? props.modelValue.legs[0] : undefined));
const firstLegDuration = computed(() => {
    if (isNonTransit.value) {
        return humanizeDuration(props.modelValue.legs?.[0].duration);
    }

    // show the humanized duration if the first leg is a walking leg
    if (!firstWalkingLeg.value) return '';

    // return short version if mobile
    return isMobile.value
        ? humanizeDuration(firstWalkingLeg.value.duration)
        : t(
              'trips.resources.tripLegLabels.walkForDuration',
              { duration: humanizeDuration(firstWalkingLeg.value.duration) },
              '{duration} walk'
          );
});

const topLabel = computed(() => {
    if (isNonTransit.value) {
        if (props.modelValue.legs?.[0].tripMode === TripMode.Foot) {
            return t('trips.resources.tripLegLabels.walkToDestination', 'Walk to destination');
        }

        if (props.modelValue.legs?.[0].tripMode === TripMode.Bicycle) {
            return t('trips.resources.tripLegLabels.bicycleToDestination', 'Bicycle to destination');
        }
    } else if (firstWalkingLeg.value) {
        return t('trips.resources.tripLegLabels.walkTo', 'Walk to');
    }

    return t('common.resources.from', 'From');
});
</script>

<style lang="scss">
@use 'vuetify/settings' as *;

.trip-pattern-summary {
    .trip-headsign {
        --title-font-size: #{calc(18px / $font-size-root) * 1rem} !important;
        --subtitle-font-size: #{calc(14px / $font-size-root) * 1rem} !important;
    }

    .bottom-div {
        margin-bottom: 0px !important;
    }
}
</style>
